import _ from "underscore";
import moment from "moment";
import Decorator from "@evertrue/client-decorator";
import SessionStore from "session-store";
import OrgStore from "org-store";
import Utils from "../../utils/utils";
// import FilterConfigStore from "./filter-config-store";

const { Query, Parameter, Property, ChildDoc } = Decorator.Search;

const _getCustomQueryType = type => {
  switch (type) {
    case "string":
      return "contains";
    case ("number", "currency"):
      return "coerce";
    case ("datetime", "date_string"):
      return "object";
    case "boolean":
      return "equals";
    default:
      return "equals";
  }
};

const _getComponentType = type => {
  switch (type) {
    case "string":
      return "string";
    case "number":
      return "number_range";
    case "currency":
      return "currency_range";
    case ("datetime", "date_string"):
      return "date_range";
    case "boolean":
      return "boolean";
    default:
      return null;
  }
};

const _unwrapLIValue = values =>
  _.map(_.pluck(values, "value"), val => {
    if (val) {
      return val.replace(/^li_/, "");
    }
    return null;
  });

let filter_config = {
  full_name: {
    query(text) {
      return Decorator.Filters.getQuery({ full_name: text });
    },
  },

  year: {
    key: "year",
    label: "Class Year",
    component_type: "year_range",
    query(val) {
      return Decorator.Filters.getQuery({ year: val });
    },
    sort: 2,
    show_tab: "both",
  },

  last_gift_date: {
    key: "last_gift_date",
    label: "Last Gift Date",
    component_type: "date_range",
    query({ gte, lte, givingCategoryLabel }) {
      const formatted_gte = gte ? moment(gte, "MM/DD/YYYY").format("YYYY-MM-DD") : "";
      const formatted_lte = lte ? moment(lte, "MM/DD/YYYY").format("YYYY-MM-DD") : "now";

      const date_range_obj = {
        ...(formatted_gte && { gte: formatted_gte }),
        ...(formatted_lte && { lte: formatted_lte }),
      };

      if (!givingCategoryLabel) {
        return Query([Parameter("must", [Property("giving.last_gift_date", date_range_obj, { type: "object" })])]);
      }

      return Query([
        Parameter(
          "must",
          [
            Property("label.untouched", givingCategoryLabel, {
              type: "equals",
              parent: "giving_categories",
              parent_type: "instance",
            }),
            !_.isEmpty(date_range_obj) &&
              Property("last_gift_date", date_range_obj, {
                type: "object",
                parent: "giving_categories",
                parent_type: "instance",
              }),
          ].filter(Boolean),
        ),
      ]);
    },
    sort: 3,
    show_tab: "both",
  },

  assigned: {
    key: "assigned",
    label: "Prospect Status",
    component_type: "tags",
    query(is_assigned) {
      return Query([
        ChildDoc(
          "assignment",
          Query([
            Parameter("must", [Property("solicitor_contact_id", Utils.jsonParse(is_assigned), { type: "exists" })]),
          ]),
        ),
      ]);
    },
    sort: 1,
    show_tab: "prospects",
    options: [{ value: "true", label: "Assigned" }, { value: "false", label: "Unassigned" }],
  },

  stages: {
    key: "stages",
    label: "Stages",
    component_type: "string",
    query(values) {
      return Query([
        ChildDoc(
          "assignment",
          Query([
            Parameter("must", [Property("assignment_stage.untouched", _.pluck(values, "value"), { type: "contains" })]),
          ]),
        ),
      ]);
    },
    sort: 8,
    show_tab: "both",
  },

  li_location: {
    key: "li_location",
    label: "LinkedIn Location",
    component_type: "string",
    query(values) {
      return Decorator.Filters.getQuery({ li_location: _unwrapLIValue(values) });
    },
    sort: 4,
    show_tab: "both",
  },

  industry: {
    key: "industry",
    label: "Company / Industry",
    component_type: "string",
    facets: {
      fields: {
        "Institutional Industries": "employments.industry.untouched",
        "LinkedIn Industries": "linkedin.industry_description.untouched",
      },
    },
    query(values) {
      const [linkedin, customer] = Array.from(_.partition(values, val => val.is_linkedin));
      return Decorator.Filters.getQuery({
        industry: {
          linkedin: _unwrapLIValue(linkedin),
          customer: _.pluck(customer, "value"),
        },
      });
    },
    sort: 5,
    show_tab: "both",
  },

  activities: {
    key: "activities",
    label: "Activities",
    component_type: "string",
    query(values) {
      return Decorator.Filters.getQuery({ activities: _.pluck(values, "value") });
    },
    sort: 6,
    show_tab: "both",
  },

  athletics: {
    key: "athletics",
    label: "Athletics",
    component_type: "string",
    query(values) {
      return Decorator.Filters.getQuery({ athletics: _.pluck(values, "value") });
    },
    sort: 7,
    show_tab: "both",
  },

  assigned_to_me: {
    key: "assigned_to_me",
    label: "Assigned to me",
    query(value) {
      const param = value ? "must" : "must_not";
      const contact_id = SessionStore.getUserContactId(OrgStore.getCurrentOrg().id);
      return Query([
        ChildDoc(
          "assignment",
          Query([Parameter(param, [Property("solicitor_contact_id", contact_id, { type: "equals" })])]),
        ),
      ]);
    },
  },
};

const FilterSetup = {
  // move to client dec
  formatPropName(prop) {
    let name = `${prop.list_name}.${prop.name}`;
    if (prop.data_type === "string") {
      name += ".untouched";
    }
    return name;
  },

  setCustomFilters(custom_props) {
    const custom_config = {};
    _.each(custom_props, prop => {
      const key_name = this.formatPropName(prop);
      custom_config[key_name] = {
        key: key_name,
        label: prop.description,
        component_type: _getComponentType(prop.data_type),
        query_type: _getCustomQueryType(prop.data_type),
        is_custom: true,
        ...prop,
      };
    });

    filter_config = _.extend({}, filter_config, custom_config);
  },

  getConfig() {
    return filter_config;
  },
};

export default FilterSetup;
